// /src/engine/ldcadShadow.ts

import * as THREE from 'three';
import type { Connector, ConnectorGender } from './connectors';
import { LDRAW_SHADOW_BASE } from './ldrawConfig';

type FileMap = Record<string, string>;

export type LdcadShadowOptions = {
  basePath?: string; // default LDRAW_SHADOW_BASE
  maxConnectorsPerPart?: number; // safety cap
};

const DEFAULT_BASE = LDRAW_SHADOW_BASE;

const CONNECTOR_CACHE = new Map<string, Promise<Connector[]>>();

const FILEMAP_PROMISES = new Map<string, Promise<FileMap | null>>();
const FILEMAP_VALUES = new Map<string, FileMap | null>();
const FILEMAP_STATUS = new Map<string, 'ok' | 'missing' | 'error'>();
const LIB_PRESENT_PROMISES = new Map<string, Promise<boolean>>();

function normalizeBasePath(p: string): string {
  if (!p) return DEFAULT_BASE;
  return p.endsWith('/') ? p : p + '/';
}

function encodePath(p: string): string {
  // Keep slashes, encode path segments individually.
  return p
    .split('/')
    .map((seg) => encodeURIComponent(seg))
    .join('/');
}

async function loadFileMap(basePath: string): Promise<FileMap | null> {
  const bp = normalizeBasePath(basePath);
  if (FILEMAP_VALUES.has(bp)) return FILEMAP_VALUES.get(bp) ?? null;

  if (!FILEMAP_PROMISES.has(bp)) {
    FILEMAP_PROMISES.set(
      bp,
      (async () => {
        try {
          const res = await fetch(`${bp}filemap.json`, { cache: 'force-cache' as any });
          if (!res.ok) {
            FILEMAP_VALUES.set(bp, null);
            FILEMAP_STATUS.set(bp, res.status === 404 ? 'missing' : 'error');
            return null;
          }
          const json = await res.json();
          if (!json || typeof json !== 'object') {
            FILEMAP_VALUES.set(bp, null);
            FILEMAP_STATUS.set(bp, 'error');
            return null;
          }
          const map = json as FileMap;
          FILEMAP_VALUES.set(bp, map);
          FILEMAP_STATUS.set(bp, 'ok');
          return map;
        } catch {
          FILEMAP_VALUES.set(bp, null);
          FILEMAP_STATUS.set(bp, 'error');
          return null;
        }
      })()
    );
  }

  return FILEMAP_PROMISES.get(bp)!;
}

async function probeShadowLibraryPresent(basePath: string): Promise<boolean> {
  const bp = normalizeBasePath(basePath);
  if (LIB_PRESENT_PROMISES.has(bp)) return LIB_PRESENT_PROMISES.get(bp)!;

  const p = (async () => {
    // Try a couple of very common files; success on either implies the library exists.
    const candidates = [`${bp}parts/3001.dat`, `${bp}p/1-4cyli.dat`];
    for (const url of candidates) {
      try {
        // HEAD first (cheap); if not allowed, fall back to GET.
        let res = await fetch(url, { method: 'HEAD', cache: 'force-cache' as any });
        if (res.ok) return true;
        if (res.status === 405 || res.status === 501) {
          res = await fetch(url, { cache: 'force-cache' as any });
          if (res.ok) return true;
        }
      } catch {
        // ignore
      }
    }
    return false;
  })();

  LIB_PRESENT_PROMISES.set(bp, p);
  return p;
}

function candidateUrlsForPart(partFile: string, basePath: string, map: FileMap | null): string[] {
  const bp = normalizeBasePath(basePath);
  const pf = partFile.replace(/\\/g, '/').trim();
  const key = pf.toLowerCase();
  const urls: string[] = [];

  // Try filemap if present.
  if (map) {
    const base = key.split('/').pop() ?? key;

    const mapped =
      map[key] ||
      map[key.replace(/^\.?\//, '')] ||
      map[base] ||
      map[`parts/${base}`] ||
      map[`p/${base}`];

    if (mapped) urls.push(`${bp}${encodePath(mapped)}`);
  }

  // Direct fallbacks.
  if (/^(parts|p)\//i.test(pf)) {
    urls.push(`${bp}${encodePath(pf)}`);
  } else {
    urls.push(`${bp}parts/${encodePath(pf)}`);
    urls.push(`${bp}p/${encodePath(pf)}`);
  }

  return Array.from(new Set(urls));
}

async function fetchFirstText(urls: string[]): Promise<string | null> {
  for (const url of urls) {
    try {
      const res = await fetch(url, { cache: 'force-cache' as any });
      if (!res.ok) continue;
      const text = await res.text();
      if (text) return text;
    } catch {
      // ignore and try next
    }
  }
  return null;
}

type GridSpec = {
  xCount: number;
  zCount: number;
  xStep: number;
  zStep: number;
  centerX: boolean;
  centerZ: boolean;
};

function parseGender(raw: string | undefined): ConnectorGender | null {
  if (!raw) return null;
  const v = raw.trim().toUpperCase();
  if (v === 'M' || v === 'MALE') return 'M';
  if (v === 'F' || v === 'FEMALE') return 'F';
  return null;
}

function parseVector3(raw: string | undefined): THREE.Vector3 | null {
  if (!raw) return null;
  const nums = raw
    .trim()
    .split(/\s+/)
    .map(Number)
    .filter((n) => Number.isFinite(n));
  if (nums.length < 3) return null;
  return new THREE.Vector3(nums[0], nums[1], nums[2]);
}

function parseOri3x3(raw: string | undefined): number[] {
  if (!raw) return [1, 0, 0, 0, 1, 0, 0, 0, 1];
  const nums = raw
    .trim()
    .split(/\s+/)
    .map(Number)
    .filter((n) => Number.isFinite(n));
  if (nums.length >= 9) return nums.slice(0, 9);
  return [1, 0, 0, 0, 1, 0, 0, 0, 1];
}

function mulOriVec(ori: number[], v: THREE.Vector3, out = new THREE.Vector3()): THREE.Vector3 {
  // ori is row-major 3x3
  const x = v.x,
    y = v.y,
    z = v.z;
  out.set(
    ori[0] * x + ori[1] * y + ori[2] * z,
    ori[3] * x + ori[4] * y + ori[5] * z,
    ori[6] * x + ori[7] * y + ori[8] * z
  );
  return out;
}

function axisFromOri(ori: number[], out = new THREE.Vector3()): THREE.Vector3 {
  // Cylinder snap metas are Y-axis oriented; axis is the transformed +Y direction.
  // v' = M * (0,1,0) = (m01, m11, m21) => indices 1,4,7.
  out.set(ori[1], ori[4], ori[7]);
  if (out.lengthSq() < 1e-12) out.set(0, 1, 0);
  return out.normalize();
}

function parseGrid(raw: string | undefined): GridSpec | null {
  if (!raw) return null;
  const t = raw.trim().split(/\s+/).filter(Boolean);
  if (t.length < 4) return null;

  let i = 0;

  const readCount = (): { count: number; centered: boolean } | null => {
    if (i >= t.length) return null;
    let token = t[i++];
    let centered = false;

    // Accept either separate "C 4" or combined "C4"
    if (/^C$/i.test(token)) {
      centered = true;
      if (i >= t.length) return null;
      token = t[i++];
    } else if (/^C\d+/i.test(token)) {
      centered = true;
      token = token.slice(1);
    }

    const count = Number.parseInt(token, 10);
    if (!Number.isFinite(count)) return null;
    return { count, centered };
  };

  const x = readCount();
  const z = readCount();
  if (!x || !z) return null;

  const xStep = Number(t[i++]);
  const zStep = Number(t[i++]);
  if (!Number.isFinite(xStep) || !Number.isFinite(zStep)) return null;

  return {
    xCount: Math.max(1, Math.floor(x.count)),
    zCount: Math.max(1, Math.floor(z.count)),
    xStep,
    zStep,
    centerX: x.centered,
    centerZ: z.centered,
  };
}

function parseBracketProps(line: string): Record<string, string> {
  const out: Record<string, string> = Object.create(null);
  const re = /\[(\w+)=([^\]]*)\]/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(line))) {
    const k = m[1].toLowerCase();
    const v = (m[2] ?? '').trim();
    out[k] = v;
  }
  return out;
}

function guessRadius(props: Record<string, string>): number {
  const r =
    Number(props.radius ?? props.rad ?? props.r);
  if (Number.isFinite(r) && r > 0) return r;

  const d =
    Number(props.diameter ?? props.diam ?? props.d);
  if (Number.isFinite(d) && d > 0) return d / 2;

  // Some metas use section lists like: shape=R 8 4 R 6 16 ...
  const shape = props.shape ?? props.sec ?? props.sections;
  if (shape) {
    const m = shape.match(/\bR\s+([0-9.]+)\s+/i);
    if (m) {
      const rr = Number(m[1]);
      if (Number.isFinite(rr) && rr > 0) return rr;
    }
  }

  return 6.0;
}

function ldrawToThreePos(v: THREE.Vector3, out = new THREE.Vector3()): THREE.Vector3 {
  // Keep consistent with ldrawMath.ts (rotate 180° around X: flip Y and Z).
  out.set(v.x, -v.y, -v.z);
  return out;
}

function ldrawToThreeDir(v: THREE.Vector3, out = new THREE.Vector3()): THREE.Vector3 {
  out.set(v.x, -v.y, -v.z);
  if (out.lengthSq() < 1e-12) out.set(0, 1, 0);
  return out.normalize();
}

function connectorsFromSnapLine(snapType: string, props: Record<string, string>, maxOut: number, out: Connector[]) {
  if (out.length >= maxOut) return;

  const group = (props.group ?? props.grp ?? props.key ?? 'cyl').trim();
  const gender =
    parseGender(props.gender) ??
    parseGender(props.gdr) ??
    parseGender(props.g) ??
    (props.genderofs ? parseGender(props.genderofs) : null) ??
    (props.gofs ? parseGender(props.gofs) : null) ??
    (snapType === 'SNAP_CLP' ? 'F' : null);

  // If we can't determine gender, matching becomes too fuzzy.
  if (!gender) return;

  const posL = parseVector3(props.pos) ?? new THREE.Vector3(0, 0, 0);
  const ori = parseOri3x3(props.ori);
  // Allow explicit axis/dir override when present (some metas use it).
  const axisL =
    parseVector3(props.axis) ??
    parseVector3(props.dir) ??
    axisFromOri(ori);
  const radius = guessRadius(props);

  const grid = parseGrid(props.grid);
  const baseId = (props.id ?? '').trim() || `${snapType}:${group}`;

  if (!grid) {
    out.push({
      kind: 'ldcad',
      source: 'ldcad',
      metaType: snapType,
      id: `${baseId}:0`,
      group,
      gender,
      pos: ldrawToThreePos(posL),
      dir: ldrawToThreeDir(axisL),
      radius,
    });
    return;
  }

  const xOrigin = grid.centerX ? -((grid.xCount - 1) * grid.xStep) / 2 : 0;
  const zOrigin = grid.centerZ ? -((grid.zCount - 1) * grid.zStep) / 2 : 0;

  const offsetSnap = new THREE.Vector3();
  const offsetPart = new THREE.Vector3();
  const tmpPos = new THREE.Vector3();

  let idx = 0;
  for (let xi = 0; xi < grid.xCount; xi++) {
    for (let zi = 0; zi < grid.zCount; zi++) {
      if (out.length >= maxOut) return;

      const ox = xOrigin + xi * grid.xStep;
      const oz = zOrigin + zi * grid.zStep;

      offsetSnap.set(ox, 0, oz);
      mulOriVec(ori, offsetSnap, offsetPart);

      tmpPos.copy(posL).add(offsetPart);

      out.push({
        kind: 'ldcad',
        source: 'ldcad',
        metaType: snapType,
        id: `${baseId}:${idx++}`,
        group,
        gender,
        pos: ldrawToThreePos(tmpPos, new THREE.Vector3()),
        dir: ldrawToThreeDir(axisL, new THREE.Vector3()),
        radius,
      });
    }
  }
}

function parseLdcadConnectorsFromText(text: string, maxConnectors = 2000): Connector[] {
  const out: Connector[] = [];
  const lines = text.replace(/\r\n/g, '\n').split('\n');

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;

    const m = line.match(/^0\s+!LDCAD\s+(SNAP_\w+)\b/i);
    if (!m) continue;

    const snapType = m[1].toUpperCase();
    if (snapType !== 'SNAP_CYL' && snapType !== 'SNAP_CLP' && snapType !== 'SNAP_FGR') continue;

    const props = parseBracketProps(line);
    connectorsFromSnapLine(snapType, props, maxConnectors, out);

    if (out.length >= maxConnectors) break;
  }

  return out;
}

export async function loadLdcadShadowConnectors(partFile: string, opts?: LdcadShadowOptions): Promise<Connector[]> {
  const basePath = normalizeBasePath(opts?.basePath ?? DEFAULT_BASE);
  const maxConnectors = Math.max(50, Math.floor(opts?.maxConnectorsPerPart ?? 2000));

  const key = `${basePath}::${partFile}`.toLowerCase();
  if (!CONNECTOR_CACHE.has(key)) {
    CONNECTOR_CACHE.set(
      key,
      (async () => {
        const map = await loadFileMap(basePath);

        // ✅ If the shadow library is actually missing, don't spam fetches for every part click.
        if (!map && FILEMAP_STATUS.get(basePath) === 'missing') {
          const present = await probeShadowLibraryPresent(basePath);
          if (!present) return [];
        }
        
        const urls = candidateUrlsForPart(partFile, basePath, map);
        const text = await fetchFirstText(urls);
        if (!text) return [];
        try {
          return parseLdcadConnectorsFromText(text, maxConnectors);
        } catch {
          return [];
        }
      })()
    );
  }

  return CONNECTOR_CACHE.get(key)!;
}
